import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { LivingSwot, SwotElement, BusinessIdea, IntelligenceHubData, OperationsHubData } from '@/src/types';
import { useAuth } from '@/src/contexts/AuthContext';
import { db, doc, onSnapshot, setDoc, handleFirestoreError, OperationType } from '@/firebase';
import { generateLivingSwot } from '@/src/services/apiServices';
import { verifyMarketTrend } from '@/src/services/verificationService';
import { generateVentureId } from '@/src/utils/formatters';

interface Props {
  idea: BusinessIdea;
  hub: 'launchpad' | 'discovery' | 'optimize';
  onUpgradePrompt: () => void;
  intelligenceData?: IntelligenceHubData;
  operationsData?: OperationsHubData;
}

const LivingSwotComponent: React.FC<Props> = ({ idea, hub, onUpgradePrompt, intelligenceData, operationsData }) => {
  const { user, isOptimize } = useAuth();
  const [swot, setSwot] = useState<LivingSwot | null>(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  // Web trend verification states
  const [verifyingFactorId, setVerifyingFactorId] = useState<string | null>(null);
  const [verifiedFactors, setVerifiedFactors] = useState<Record<string, { status: 'confirmed' | 'unconfirmed', evidence: string }>>({});

  const ventureId = generateVentureId(idea.title, idea.locationName, idea.id);

  useEffect(() => {
    if (!user || !ventureId) {
      setLoading(false);
      return;
    }

    const path = hub === 'optimize' 
      ? `users/${user.uid}/optimize/businesses/${ventureId}/swot`
      : `users/${user.uid}/savedIdeas/${ventureId}/swot`;

    const normalizeSwot = (data: any): LivingSwot | null => {
      if (!data) return null;
      const now = Date.now();
      const toElements = (arr: any[]): SwotElement[] => {
        if (!Array.isArray(arr)) return [];
        return arr.map((item: any, idx: number) => {
          if (typeof item === 'string') {
            return {
              id: `swot-${idx}-${now}`,
              text: item,
              score: 50 + Math.floor(Math.random() * 30),
              confidence: 0.65 + Math.random() * 0.25,
              source: 'AI Analysis',
              lastUpdated: now,
              status: 'new',
              impact: 0.4 + Math.random() * 0.4
            };
          }
          return {
            id: item.id || `swot-${idx}-${now}`,
            text: item.text || String(item),
            score: typeof item.score === 'number' ? item.score : 50,
            confidence: typeof item.confidence === 'number' ? item.confidence : 0.7,
            source: item.source || 'AI Analysis',
            lastUpdated: item.lastUpdated || now,
            status: item.status || 'new',
            impact: typeof item.impact === 'number' ? item.impact : 0.5
          };
        });
      };
      // Handle both nested { swot: { strengths: [] } } and flat { strengths: [] }
      const source = data.swot || data;
      return {
        id: data.id || `swot-${data.ventureId || 'unknown'}`,
        ventureId: data.ventureId || 'unknown',
        strengths: toElements(source.strengths || data.strengths || []),
        weaknesses: toElements(source.weaknesses || data.weaknesses || []),
        opportunities: toElements(source.opportunities || data.opportunities || []),
        threats: toElements(source.threats || data.threats || []),
        narrative: data.narrative || data.summary || '',
        lastUpdated: data.lastUpdated || now,
        version: typeof data.version === 'number' ? data.version : 1
      };
    };

    const unsubscribe = onSnapshot(doc(db, path, 'current'), (snapshot) => {
      if (snapshot.exists()) {
        setSwot(normalizeSwot(snapshot.data()));
      } else {
        setSwot(null);
      }
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.GET, path);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user, ventureId, hub]);

  const handleRecalculate = async () => {
    if (!user || !ventureId) return;
    setRecalculating(true);
    
    try {
      const result = await generateLivingSwot(idea, idea.locationName || 'this district', {
  intelligence: intelligenceData,
  operations: operationsData
}) as LivingSwot;

      const path = hub === 'optimize' 
        ? `users/${user.uid}/optimize/businesses/${ventureId}/swot`
        : `users/${user.uid}/savedIdeas/${ventureId}/swot`;
      
      const newSwot: LivingSwot = {
        ...result,
        lastUpdated: Date.now(),
        version: (swot?.version || 0) + 1
      };

      await setDoc(doc(db, path, 'current'), newSwot);
      
      // Also save to history
      const historyId = new Date().toISOString().replace(/[:.]/g, '-');
      const historyPath = `${path}/history/${historyId}`;
      await setDoc(doc(db, historyPath), {
        ...newSwot,
        id: historyId,
        timestamp: Date.now()
      });

    } catch (err) {
      console.error('Recalculation failed:', err);
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 bg-[#3a3430] border border-white/[0.06] rounded-xl">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-[#F26522] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 animate-pulse font-heading">Initializing SWOT Protocol...</p>
        </div>
      </div>
    );
  }

  if (!swot && !recalculating) {
    return (
      <div className="p-10 bg-[#3a3430] border border-white/[0.06] rounded-xl text-center space-y-5">
        <div className="w-12 h-12 bg-[#322D29] border border-white/[0.06] rounded-lg flex items-center justify-center mx-auto text-[#F26522]">
          <i className="fas fa-brain text-xl"></i>
        </div>
        <div className="max-w-md mx-auto space-y-2">
          <h3 className="text-base font-bold text-white uppercase tracking-tight font-heading">Living SWOT Analysis</h3>
          <p className="text-white/60 text-xs font-dm leading-relaxed">Generate a real-time strategic analysis by synthesizing market data, competitive intelligence, and operational projections.</p>
        </div>
        <button 
          onClick={handleRecalculate}
          className="px-6 py-3 bg-[#F26522] text-white font-bold uppercase text-[9px] tracking-[0.25em] hover:bg-[#F26522]/90 transition-all rounded-lg cursor-pointer font-heading"
        >
          Initialize Analysis
        </button>
      </div>
    );
  }

  const Quadrant = ({ title, elements, color, icon }: { title: string, elements: SwotElement[], color: string, icon: string }) => (
    <div className="bg-[#3a3430] border border-white/[0.06] rounded-xl p-5 flex flex-col h-full relative overflow-hidden">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-[#322D29] border border-white/[0.06] flex items-center justify-center text-white">
            <i className={`fas ${icon} text-[11px] ${color}`}></i>
          </div>
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">{title}</h3>
        </div>
        <span className="text-[10px] font-medium text-white/40 font-heading">{elements.length} Factors</span>
      </div>

      <div className="space-y-3 flex-1">
        {elements.map((el, idx) => (
          <div key={`swot-${el.id || idx}-${idx}`} className="p-3.5 bg-[#322D29] border border-white/[0.06] hover:border-white/20 transition-all relative rounded-lg group/item">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-2 flex-wrap">
                {el.status === 'new' && (
                  <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[7px] font-bold uppercase tracking-widest rounded animate-pulse">New</span>
                )}
                {el.status === 'updated' && (
                  <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-300 border border-blue-500/30 text-[7px] font-bold uppercase tracking-widest rounded">Updated</span>
                )}
                <span className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">{el.source}</span>
                {/* Web Trend Validation badge */}
                {(title === "Opportunities" || title === "Threats") && (
                  <div className="ml-1 inline-flex items-center">
                    {verifiedFactors[el.id || el.text] ? (
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded ${verifiedFactors[el.id || el.text].status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`} title={verifiedFactors[el.id || el.text].evidence}>
                        <span className={`w-1.5 h-1.5 rounded-full ${verifiedFactors[el.id || el.text].status === 'confirmed' ? 'bg-emerald-400 animate-pulse' : 'bg-white/40'}`}></span>
                        {verifiedFactors[el.id || el.text].status.toUpperCase()}
                      </span>
                    ) : (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!user) return;
                          setVerifyingFactorId(el.id || el.text);
                          try {
                            const res = await verifyMarketTrend(el.text, idea.locationName || 'Kampala', user.uid);
                            setVerifiedFactors(prev => ({ ...prev, [el.id || el.text]: res }));
                          } catch (err) {
                            console.error(err);
                          } finally {
                            setVerifyingFactorId(null);
                          }
                        }}
                        disabled={verifyingFactorId !== null}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[7.5px] font-bold uppercase tracking-widest rounded bg-white/5 hover:bg-[#F26522]/20 hover:text-[#F26522] text-white/60 border border-white/[0.06] transition-colors cursor-pointer"
                      >
                        {verifyingFactorId === (el.id || el.text) ? (
                          <>
                            <i className="fas fa-circle-notch animate-spin text-[6px]"></i>
                            Verifying...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-globe text-[6px]"></i>
                            Verify
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex flex-col items-end">
                  <span className="text-[9px] font-bold text-white font-heading">{el.score}</span>
                  <span className="text-[7px] font-medium text-white/40 uppercase tracking-tighter">Impact</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-white/80 leading-relaxed pr-6 font-dm">{el.text}</p>

            {/* Real-time Evidence Panel */}
            {verifiedFactors[el.id || el.text] && (
              <div className="mt-2 text-left p-2.5 bg-emerald-500/10 border border-emerald-500/20 text-[10px] text-white/70 leading-normal rounded font-dm">
                <div className="flex items-center gap-1.5 font-bold text-emerald-300 uppercase tracking-wider text-[8px] mb-1 font-heading">
                  <i className="fas fa-circle-check text-emerald-400"></i>
                  Verified Intel Evidence
                </div>
                {verifiedFactors[el.id || el.text].evidence}
              </div>
            )}

            {/* Confidence Meter */}
            <div className="mt-2.5 flex items-center gap-2">
              <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-1000 ${el.confidence > 0.8 ? 'bg-emerald-400' : el.confidence > 0.5 ? 'bg-[#F26522]' : 'bg-white/40'}`}
                  style={{ width: `${el.confidence * 100}%` }}
                ></div>
              </div>
              <span className="text-[8px] font-medium text-white/40">{(el.confidence * 100).toFixed(0)}% Conf.</span>
            </div>

            {isOptimize && (
              <button className="absolute top-3 right-3 opacity-0 group-hover/item:opacity-100 transition-opacity text-white/40 hover:text-[#F26522]">
                <i className="fas fa-comment-dots text-xs"></i>
              </button>
            )}
          </div>
        ))}

        {elements.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-white/30 border border-dashed border-white/10 rounded-lg">
            <i className={`fas ${icon} text-xl mb-1.5 opacity-20`}></i>
            <p className="text-[9px] font-bold uppercase tracking-widest font-heading">No data detected</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-5 bg-[#3a3430] p-6 sm:p-8 border border-white/[0.06] rounded-xl relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-1.5 h-1.5 bg-[#F26522] rounded-full animate-pulse"></div>
            <span className="text-[9px] font-bold uppercase tracking-[0.25em] text-[#F26522] font-heading">Living SWOT Protocol</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight font-heading">Real-Time Venture Intelligence</h2>
          <p className="text-white/60 text-xs sm:text-sm mt-1.5 max-w-xl font-dm leading-relaxed">
            Dynamic analysis engine synchronizing market shifts, competitive moves, and operational performance into actionable strategy.
          </p>
        </div>
        <div className="flex items-center gap-4 relative z-10">
          <div className="text-right hidden sm:block">
            <p className="text-[8px] font-bold uppercase tracking-widest text-white/40 font-heading">Last Sync</p>
            <p className="text-xs font-medium text-white font-dm">{swot ? new Date(swot.lastUpdated).toLocaleTimeString() : 'Never'}</p>
          </div>
          <button 
            onClick={handleRecalculate}
            disabled={recalculating}
            className="px-5 py-3 bg-[#F26522] text-white font-bold uppercase text-[9px] tracking-[0.2em] rounded-lg hover:bg-[#F26522]/90 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed font-heading"
          >
            {recalculating ? (
              <i className="fas fa-gear animate-spin"></i>
            ) : (
              <i className="fas fa-sync-alt"></i>
            )}
            Recalculate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Quadrant title="Strengths" elements={swot?.strengths || []} color="text-emerald-400" icon="fa-shield-halved" />
        <Quadrant title="Weaknesses" elements={swot?.weaknesses || []} color="text-red-400" icon="fa-triangle-exclamation" />
        <Quadrant title="Opportunities" elements={swot?.opportunities || []} color="text-blue-400" icon="fa-arrow-trend-up" />
        <Quadrant title="Threats" elements={swot?.threats || []} color="text-orange-400" icon="fa-bolt-lightning" />
      </div>

      {swot?.narrative && isOptimize && (
        <div className="bg-[#3a3430] border border-white/[0.06] rounded-xl p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <i className="fas fa-robot text-[#F26522]"></i>
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white font-heading">AI Strategic Narrative</h3>
          </div>
          <p className="text-white/80 text-xs sm:text-sm leading-relaxed whitespace-pre-wrap italic font-dm">
            "{swot.narrative}"
          </p>
        </div>
      )}

      {swot?.narrative && !isOptimize && (
        <div className="bg-[#3a3430] border border-dashed border-white/10 rounded-xl p-6 text-center space-y-3">
          <div className="w-8 h-8 bg-[#322D29] border border-white/[0.06] rounded-lg flex items-center justify-center mx-auto text-white/40">
            <i className="fas fa-lock text-xs"></i>
          </div>
          <div className="space-y-1">
            <p className="text-[9px] font-bold uppercase tracking-widest text-white/40 font-heading">AI Strategic Narrative Locked</p>
            <p className="text-xs text-white/60 font-dm">Upgrade to <span className="text-[#F26522] font-bold">Premium Pro</span> to unlock deep strategic narratives and team annotations.</p>
          </div>
          <button 
            onClick={onUpgradePrompt}
            className="text-[9px] font-bold uppercase tracking-widest text-[#F26522] hover:underline font-heading cursor-pointer"
          >
            Upgrade Now
          </button>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-[#3a3430] border border-white/[0.06] rounded-xl gap-3">
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
            <span className="text-[9px] font-medium uppercase tracking-widest text-white/60 font-heading">High Confidence ({'>'}80%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-[#F26522] rounded-full"></div>
            <span className="text-[9px] font-medium uppercase tracking-widest text-white/60 font-heading">Moderate Confidence (50-80%)</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-white/40">
          <i className="fas fa-info-circle text-[9px]"></i>
          <span className="text-[8px] font-medium uppercase tracking-widest font-heading">Version {swot?.version || 1.0} • 90-Day History Active</span>
        </div>
      </div>
    </div>
  );
};

export default LivingSwotComponent;
